// ═══════════════════════════════════════════════════════════════════════════
// PAGE: HISTORIQUE DES VERSIONS D'UNE ACTION (traçabilité — juin 2026)
// ═══════════════════════════════════════════════════════════════════════════
//
// Route : `#action/:id/versions`. Ouverte par le CTA « Historique » de la page
// d'édition. Restitution validée avec Christophe :
//   • timeline en haut pour faire défiler les versions ;
//   • 3 colonnes : version N (gauche), version N-1 (milieu), notes (droite —
//     fil chronologique ancré sur la date de la version N) ;
//   • CTA « Fermer » en bas (retour à la fiche action).
//
// Tous les champs sont TOUJOURS affichés (même sans changement) ; les champs
// modifiés entre N et N-1 sont surlignés (accent), les ajouts en vert, les
// suppressions barrées en rouge. La logique pure de diff vit dans
// domain/action-version-diff.ts (testée Vitest) ; ce module n'est que la glu DOM.

import { definePage } from '../router.js';
import {
  changedFields,
  diffUserList,
  diffCodeList,
  noteAnchorIndex,
  type ActionSnapshot,
  type RaciUser,
  type CodeLabel,
  type RaciRole,
  type DiffField,
} from '../domain/action-version-diff.js';

interface VersionMeta {
  version_no: number;
  changed_at?: string | null;
  changed_by_trigram?: string | null;
  changed_by_name?: string | null;
}
interface VersionFull extends VersionMeta {
  snapshot: ActionSnapshot;
}
interface NoteFromApi {
  id: number;
  author_name?: string | null;
  created_at?: string | null;
  comment: string;
}

const ACCENT = 'var(--accent)';
const GREEN = '#147A57';
const GREEN_BG = '#e3f4ea';
const RED = '#b3261e';
const RED_BG = '#fbe9e7';

// ── Rendu des valeurs ───────────────────────────────────────────────────────

function userChip(u: RaciUser, variant: 'kept' | 'added' | 'removed'): string {
  const tri = esc((u.trigram || u.name || '?') as string);
  const title = esc((u.name || u.trigram || '') as string);
  if (variant === 'added') {
    return `<span title="${title}" style="display:inline-block;background:${GREEN_BG};color:${GREEN};font-size:11px;padding:2px 7px;border-radius:10px;margin:2px">${tri}</span>`;
  }
  if (variant === 'removed') {
    return `<span title="${title}" style="display:inline-block;background:${RED_BG};color:${RED};text-decoration:line-through;font-size:11px;padding:2px 7px;border-radius:10px;margin:2px">${tri}</span>`;
  }
  // « kept » : neutre. On n'applique PAS les couleurs RACI standard ici ; seules
  // les règles de modification (ajout vert / suppression rouge) colorent.
  return `<span title="${title}" style="display:inline-block;background:var(--bg2);color:var(--text2);font-size:11px;padding:2px 7px;border-radius:10px;margin:2px">${tri}</span>`;
}

function renderUsers(cur: RaciUser[] | undefined, prev: RaciUser[] | null): string {
  const d = diffUserList(cur, prev);
  const chips = [
    ...d.kept.map((u) => userChip(u, 'kept')),
    ...d.added.map((u) => userChip(u, 'added')),
    ...d.removed.map((u) => userChip(u, 'removed')),
  ];
  return chips.length ? chips.join('') : `<span style="color:var(--text3);font-size:11px">—</span>`;
}

function codeChip(e: CodeLabel, variant: 'kept' | 'added' | 'removed', extra = ''): string {
  const code = esc(((e.code ?? '') || '?') as string);
  const title = esc(((e.label ?? '') as string) + extra);
  const common =
    'display:inline-block;font-size:11px;padding:1px 6px;border-radius:var(--radius);margin:2px';
  if (variant === 'added') {
    return `<span title="${title}" style="${common};background:${GREEN_BG};color:${GREEN}">${code}${extra ? ' ⬆' : ''}</span>`;
  }
  if (variant === 'removed') {
    return `<span title="${title}" style="${common};background:${RED_BG};color:${RED};text-decoration:line-through">${code}</span>`;
  }
  return `<span title="${title}" style="${common};border:1px solid var(--border2);color:var(--text2)">${code}${extra ? ' ⬆' : ''}</span>`;
}

function renderCodes(cur: CodeLabel[] | undefined, prev: CodeLabel[] | null): string {
  const d = diffCodeList(cur, prev);
  const mark = (e: CodeLabel): string =>
    'escalated' in e && (e as { escalated?: boolean }).escalated ? ' (escaladée)' : '';
  const chips = [
    ...d.kept.map((e) => codeChip(e, 'kept', mark(e))),
    ...d.added.map((e) => codeChip(e, 'added', mark(e))),
    ...d.removed.map((e) => codeChip(e, 'removed')),
  ];
  return chips.length ? chips.join('') : `<span style="color:var(--text3);font-size:11px">—</span>`;
}

function statusValue(s: ActionSnapshot['status']): string {
  if (!s) return `<span style="color:var(--text3)">—</span>`;
  // Pas de pastille de couleur du statut : on n'applique pas les couleurs
  // standard d'état ici, uniquement le surlignage de modification (porté par
  // l'accent de la cellule).
  return esc(s.label);
}

// Jauge d'avancement (lecture seule), équivalent visuel du curseur de la page MAJ.
function progressGauge(p: number | null | undefined): string {
  const v = Math.max(0, Math.min(100, Math.round(p || 0)));
  return `<div style="display:flex;align-items:center;gap:8px">
      <div style="flex:1;height:6px;background:var(--bg2);border-radius:4px;overflow:hidden"><div style="width:${v}%;height:100%;background:var(--accent)"></div></div>
      <span style="font-size:12px;min-width:32px;text-align:right">${v} %</span>
    </div>`;
}

// Wrappe une ligne de champ pleine largeur ; accent à gauche si modifié.
function fieldRow(label: string, valueHtml: string, changed: boolean): string {
  const accent = changed
    ? `border-left:3px solid ${ACCENT};padding-left:7px;margin-left:-10px;`
    : '';
  return `<div style="margin-bottom:8px;${accent}">
      <div style="font-size:11px;color:var(--text2);margin-bottom:1px">${esc(label)}</div>
      <div style="font-size:13px;color:var(--text)">${valueHtml}</div>
    </div>`;
}

// Cellule de champ destinée à une grille 2 colonnes (sans marge négative).
function fieldCell(label: string, valueHtml: string, changed: boolean): string {
  const accent = changed ? `border-left:3px solid ${ACCENT};padding-left:7px;` : '';
  return `<div style="${accent}">
      <div style="font-size:11px;color:var(--text2);margin-bottom:1px">${esc(label)}</div>
      <div style="font-size:13px;color:var(--text)">${valueHtml}</div>
    </div>`;
}

function gridRow2(a: string, b: string): string {
  return `<div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:8px">${a}${b}</div>`;
}

function raciBlock(snap: ActionSnapshot, prev: ActionSnapshot | null, changed: boolean): string {
  const roles: Array<[RaciRole, string]> = [
    ['r', t('actionModal.raci.r')],
    ['a', t('actionModal.raci.a')],
    ['c', t('actionModal.raci.c')],
    ['i', t('actionModal.raci.i')],
  ];
  const lines = roles
    .map(
      ([role, lbl]) =>
        `<div style="margin-bottom:2px"><span style="color:var(--text3);font-size:11px;margin-right:4px">${esc(lbl)}</span>${renderUsers(
          snap.raci?.[role],
          prev ? (prev.raci?.[role] ?? []) : null,
        )}</div>`,
    )
    .join('');
  const accent = changed
    ? `border-left:3px solid ${ACCENT};padding-left:7px;margin-left:-10px;`
    : '';
  return `<div style="margin-bottom:8px;${accent}">
      <div style="font-size:11px;color:var(--text2);margin-bottom:2px">${esc(t('pageActionVersions.field.raci'))}</div>
      ${lines}
    </div>`;
}

function renderCard(
  snap: ActionSnapshot,
  prev: ActionSnapshot | null,
  meta: VersionMeta,
  roleLabel: string,
): string {
  const c = changedFields(snap, prev);
  const has = (f: DiffField): boolean => c.has(f);
  const author = meta.changed_by_trigram || '—';
  const when = fmtDateTimeLocal(meta.changed_at) || '';
  return `<div style="background:var(--bg);border:1px solid var(--border);border-radius:var(--radius);padding:12px 14px;min-width:0">
      <div style="display:flex;align-items:baseline;justify-content:space-between;margin-bottom:10px;gap:8px">
        <span style="font-weight:600;font-size:13px">${esc(t('pageActionVersions.versionN', { n: meta.version_no }))} <span style="font-weight:400;color:var(--text3);font-size:11px">${esc(roleLabel)}</span></span>
        <span style="font-size:11px;color:var(--text3)" title="${esc(meta.changed_by_name || '')}">${esc(author)} · ${esc(when)}</span>
      </div>
      ${fieldRow(t('pageActionVersions.field.title'), esc(snap.text || '—'), has('text'))}
      ${fieldRow(t('pageActionVersions.field.priority'), esc(priorityLabel(snap.priority)), has('priority'))}
      ${gridRow2(
        fieldCell(
          t('pageActionVersions.field.deliverable'),
          esc(snap.expected_deliverable || '—'),
          has('expected_deliverable'),
        ),
        fieldCell(
          t('pageActionVersions.field.externalLink'),
          esc(snap.external_link || '—'),
          has('external_link'),
        ),
      )}
      ${gridRow2(
        fieldCell(
          t('pageActionVersions.field.deadline'),
          esc(fmtDateLocal(snap.deadline)),
          has('deadline'),
        ),
        fieldCell(
          t('pageActionVersions.field.deadlineRevised'),
          esc(fmtDateLocal(snap.deadline_revised)),
          has('deadline_revised'),
        ),
      )}
      ${gridRow2(
        fieldCell(t('pageActionVersions.field.status'), statusValue(snap.status), has('status')),
        fieldCell(
          t('pageActionVersions.field.progress'),
          progressGauge(snap.progress),
          has('progress'),
        ),
      )}
      ${raciBlock(snap, prev, has('raci'))}
      ${fieldRow(t('pageActionVersions.field.risks'), renderCodes(snap.risks, prev ? (prev.risks ?? []) : null), has('risks'))}
      ${fieldRow(t('pageActionVersions.field.projects'), renderCodes(snap.projects, prev ? (prev.projects ?? []) : null), has('projects'))}
      ${fieldRow(t('pageActionVersions.field.committees'), renderCodes(snap.committees, prev ? (prev.committees ?? []) : null), has('committees'))}
      ${fieldRow(t('pageActionVersions.field.structures'), renderCodes(snap.structures, prev ? (prev.structures ?? []) : null), has('structures'))}
    </div>`;
}

function renderEmptyCard(message: string): string {
  return `<div style="background:var(--bg);border:1px dashed var(--border2);border-radius:var(--radius);padding:20px 14px;color:var(--text3);font-size:12px;display:flex;align-items:center;justify-content:center;text-align:center">${esc(message)}</div>`;
}

function renderTimeline(
  metas: VersionMeta[],
  selectedNo: number,
  prevNo: number | null,
  collapsed: boolean,
): string {
  // Largeur fixe par graduation : la frise se comporte en règle crantée qui
  // défile horizontalement quand les versions débordent du bandeau.
  const PITCH = 86;
  const cells = metas
    .map((m) => {
      const isN = m.version_no === selectedNo;
      const isPrev = m.version_no === prevNo;
      const fill = isN ? ACCENT : isPrev ? 'var(--accent-bg)' : 'var(--bg)';
      const ring = isN || isPrev ? ACCENT : 'var(--border2)';
      const size = isN ? 14 : 11;
      // Cran (trait vertical descendant de la règle) : plus haut/accentué pour N.
      const tickColor = isN || isPrev ? ACCENT : 'var(--border2)';
      const tickH = isN ? 12 : 7;
      const label = isN
        ? t('pageActionVersions.tlN')
        : isPrev
          ? t('pageActionVersions.tlPrev')
          : '';
      return `<button type="button" class="av-dot" data-vno="${m.version_no}" title="${esc(fmtDateTimeLocal(m.changed_at) || '')}" style="flex:0 0 ${PITCH}px;width:${PITCH}px;background:none;border:none;cursor:pointer;padding:0;display:flex;flex-direction:column;align-items:center">
          <div style="width:2px;height:${tickH}px;background:${tickColor}"></div>
          <div style="width:${size}px;height:${size}px;border-radius:50%;background:${fill};border:1.5px solid ${ring};margin:3px 0 4px"></div>
          <div style="font-size:11px;color:${isN ? ACCENT : 'var(--text2)'};font-weight:${isN ? 600 : 400}">v${m.version_no}</div>
          <div style="font-size:10px;color:var(--text3)">${esc(fmtDateLocal(m.changed_at))}</div>
          <div style="font-size:10px;color:${ACCENT};height:12px">${esc(label)}</div>
        </button>`;
    })
    .join('');
  const chevron = collapsed ? '›' : '⌄';
  return `<div style="background:var(--bg);border:1px solid var(--border);border-radius:var(--radius);margin-bottom:12px">
      <div style="display:flex;align-items:center;justify-content:space-between;padding:6px 10px">
        <span style="font-size:11px;color:var(--text2);font-weight:500">${esc(t('pageActionVersions.timelineLabel'))}</span>
        <button type="button" data-av-timeline-toggle aria-label="${esc(t('pageActionVersions.timelineToggle'))}" aria-expanded="${collapsed ? 'false' : 'true'}" style="background:none;border:none;cursor:pointer;color:var(--text2);font-size:15px;line-height:1;padding:2px 6px">${chevron}</button>
      </div>
      <div id="av-timeline-body" style="display:${collapsed ? 'none' : 'block'};padding:0 10px 10px;overflow-x:auto">
        <div style="display:flex;align-items:flex-start;border-top:2px solid var(--border2);min-width:100%;width:max-content">${cells}</div>
      </div>
    </div>`;
}

function renderNotesColumn(notes: NoteFromApi[], anchorIdx: number, selectedNo: number): string {
  if (notes.length === 0) {
    return `<div style="background:var(--bg);border:1px solid var(--border);border-radius:var(--radius);padding:12px 14px;min-width:0">
        <div style="display:flex;align-items:center;gap:5px;margin-bottom:9px;font-weight:600;font-size:13px">${esc(t('pageActionVersions.notes'))}</div>
        <div style="color:var(--text3);font-size:12px">${esc(t('pageActionVersions.notesEmpty'))}</div>
      </div>`;
  }
  const items: string[] = [];
  notes.forEach((n, idx) => {
    if (idx === anchorIdx) {
      items.push(
        `<div data-note-anchor="1" style="display:flex;align-items:center;gap:6px;margin:6px 0;color:${ACCENT};font-size:11px"><span style="flex:1;height:1px;background:var(--accent)"></span><span>${esc(t('pageActionVersions.notesAnchor', { n: selectedNo }))}</span></div>`,
      );
    }
    items.push(
      `<div style="background:var(--bg2);border:1px solid var(--border);border-radius:var(--radius);padding:7px 9px;margin-bottom:5px">
          <div style="font-size:11px;font-weight:500;color:var(--text2);margin-bottom:3px">${esc(n.author_name || '—')} · ${esc(fmtDateTimeLocal(n.created_at) || '')}</div>
          <div style="font-size:12px;color:var(--text);white-space:pre-wrap">${esc(n.comment)}</div>
        </div>`,
    );
  });
  if (anchorIdx >= notes.length) {
    items.push(
      `<div data-note-anchor="1" style="display:flex;align-items:center;gap:6px;margin:6px 0;color:${ACCENT};font-size:11px"><span style="flex:1;height:1px;background:var(--accent)"></span><span>${esc(t('pageActionVersions.notesAnchor', { n: selectedNo }))}</span></div>`,
    );
  }
  return `<div style="background:var(--bg);border:1px solid var(--border);border-radius:var(--radius);padding:12px 14px;min-width:0">
      <div style="display:flex;align-items:center;gap:5px;margin-bottom:9px;font-weight:600;font-size:13px">${esc(t('pageActionVersions.notes'))}</div>
      <div id="av-notes-scroll" style="max-height:60vh;overflow-y:auto">${items.join('')}</div>
    </div>`;
}

pages.actionVersions = definePage<'action/:id/versions'>(async ({ id }) => {
  setHeader(esc(t('pageActionVersions.title', { id })), '');
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);

  const uid = await ensureCurrentUserId();
  if (!uid) {
    setContent(`<div class="error">${esc(t('pageActions.notAuth'))}</div>`);
    return;
  }

  let metas: VersionMeta[] = [];
  let notesDesc: NoteFromApi[] = [];
  try {
    const [versionsResp, notesResp] = (await Promise.all([
      api.getActionVersions(id),
      api.getActionNotes(id).catch(() => []),
    ])) as [{ versions?: VersionMeta[] } | undefined, NoteFromApi[]];
    metas = (versionsResp?.versions || []).slice().sort((a, b) => a.version_no - b.version_no);
    // Notes de la plus récente à la plus ancienne (l'API les renvoie asc).
    notesDesc = (notesResp || [])
      .slice()
      .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error" style="padding:20px">${esc(msg)}</div>`);
    return;
  }

  if (metas.length === 0) {
    setContent(
      `<div class="error" style="padding:20px"><p>${esc(t('pageActionVersions.empty'))}</p>
        <p style="margin-top:12px"><a href="#action/${id}" class="btn btn-secondary">${esc(t('pageActionVersions.close'))}</a></p></div>`,
    );
    return;
  }

  const snapCache = new Map<number, ActionSnapshot>();
  async function snapshotFor(no: number): Promise<ActionSnapshot> {
    const cached = snapCache.get(no);
    if (cached) return cached;
    const full = (await api.getActionVersion(id, no)) as VersionFull;
    snapCache.set(no, full.snapshot);
    return full.snapshot;
  }

  const lastMeta = metas[metas.length - 1];
  let selectedNo = lastMeta ? lastMeta.version_no : 0;
  // État de pli du bandeau timeline, conservé entre les re-rendus.
  let timelineCollapsed = false;

  async function renderSelected(): Promise<void> {
    const idx = metas.findIndex((m) => m.version_no === selectedNo);
    const nMeta = metas[idx];
    if (!nMeta) return;
    const prevMeta = idx > 0 ? (metas[idx - 1] ?? null) : null;

    const nSnap = await snapshotFor(nMeta.version_no);
    const prevSnap = prevMeta ? await snapshotFor(prevMeta.version_no) : null;

    const anchorIdx = noteAnchorIndex(notesDesc, nMeta.changed_at);

    const middle = prevMeta
      ? renderCard(prevSnap as ActionSnapshot, null, prevMeta, t('pageActionVersions.roleNMinus1'))
      : renderEmptyCard(t('pageActionVersions.noPrevious'));

    const breadcrumb = `<nav style="font-size:12px;color:var(--text2);margin-bottom:12px">
        <a href="#actions" style="color:var(--accent);text-decoration:underline">${esc(t('vocabulary.action_plural'))}</a>
        <span style="color:var(--text3);margin:0 6px">›</span>
        <a href="#action/${id}" style="color:var(--accent);text-decoration:underline">#${id}</a>
        <span style="color:var(--text3);margin:0 6px">›</span>
        <span style="color:var(--text2);font-weight:500">${esc(t('pageActionVersions.breadcrumb'))}</span>
      </nav>`;

    setContent(
      `<div style="max-width:1080px;margin:0 auto;padding:14px 12px 24px">
        ${breadcrumb}
        ${renderTimeline(metas, selectedNo, prevMeta ? prevMeta.version_no : null, timelineCollapsed)}
        <div style="display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr) minmax(0,0.85fr);gap:10px">
          ${renderCard(nSnap, prevSnap, nMeta, t('pageActionVersions.roleN'))}
          ${middle}
          ${renderNotesColumn(notesDesc, anchorIdx, selectedNo)}
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:14px;margin-top:12px;font-size:11px;color:var(--text2)">
          <span><span style="display:inline-block;width:10px;height:10px;background:${ACCENT};border-radius:2px;vertical-align:-1px;margin-right:4px"></span>${esc(t('pageActionVersions.legendChanged'))}</span>
          <span><span style="display:inline-block;width:10px;height:10px;background:${GREEN_BG};border:1px solid ${GREEN};border-radius:2px;vertical-align:-1px;margin-right:4px"></span>${esc(t('pageActionVersions.legendAdded'))}</span>
          <span><span style="display:inline-block;width:10px;height:10px;background:${RED_BG};border:1px solid ${RED};border-radius:2px;vertical-align:-1px;margin-right:4px"></span>${esc(t('pageActionVersions.legendRemoved'))}</span>
          <span style="color:var(--text3)">${esc(t('pageActionVersions.legendHover'))}</span>
        </div>
        <div style="display:flex;justify-content:flex-end;margin-top:16px;padding-top:14px;border-top:1px solid var(--border)">
          <a href="#action/${id}" class="btn btn-primary" style="width:170px;text-align:center;text-decoration:none">${esc(t('pageActionVersions.close'))}</a>
        </div>
      </div>`,
    );

    // Branche la navigation par la timeline.
    document.querySelectorAll<HTMLElement>('.av-dot').forEach((el) => {
      el.addEventListener('click', () => {
        const vno = Number(el.getAttribute('data-vno'));
        if (!Number.isNaN(vno) && vno !== selectedNo) {
          selectedNo = vno;
          void renderSelected();
        }
      });
    });

    // Plier / déplier le bandeau timeline (sans re-render complet).
    const tlToggle = document.querySelector<HTMLElement>('[data-av-timeline-toggle]');
    const tlBody = document.getElementById('av-timeline-body');
    if (tlToggle && tlBody) {
      tlToggle.addEventListener('click', () => {
        timelineCollapsed = !timelineCollapsed;
        tlBody.style.display = timelineCollapsed ? 'none' : 'block';
        tlToggle.textContent = timelineCollapsed ? '›' : '⌄';
        tlToggle.setAttribute('aria-expanded', timelineCollapsed ? 'false' : 'true');
      });
    }

    // Ancre la colonne des notes sur la date de la version N.
    const anchorEl = document.querySelector<HTMLElement>('[data-note-anchor]');
    if (anchorEl) anchorEl.scrollIntoView({ block: 'center' });
  }

  await renderSelected();
});

export {};
